import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Code2, Eye, LayoutList, Loader2, Monitor, Pencil, Plus, RefreshCw, SendHorizonal, Smartphone, Trash2, X } from "lucide-react";
import {
  CATEGORIES,
  CHANNELS,
  channelLabel,
  errorMessage,
  formatDate,
  isDeviceChannel,
  parseBlocks,
  serializeBlocks,
  type Category,
  type Channel,
  type Template,
  type TemplateBlock,
  type TemplatePreview,
  type TemplatesResponse,
} from "@/lib/comms";
import { EmptyState, ErrorState, TableSkeleton } from "./shared";
import BlockEditor from "./BlockEditor";

const PREVIEW_WIDTHS = [
  { id: "mobile", label: "Mobile", px: 375, icon: Smartphone },
  { id: "desktop", label: "Desktop", px: 640, icon: Monitor },
] as const;

interface Draft {
  id: string | null;
  name: string;
  channel: Channel;
  category: Category;
  subject: string;
  previewText: string;
  body: string;
  ctaText: string;
  ctaUrl: string;
  whatsappContentSid: string;
  whatsappVariables: string[];
  pushUrl: string;
}

const EMPTY_DRAFT: Draft = {
  id: null,
  name: "",
  channel: "email",
  category: "learning",
  subject: "",
  previewText: "",
  body: "",
  ctaText: "",
  ctaUrl: "",
  whatsappContentSid: "",
  whatsappVariables: [],
  pushUrl: "",
};

function toDraft(t: Template): Draft {
  return {
    id: t.id,
    name: t.name,
    channel: t.channel,
    category: t.category,
    subject: t.subject || "",
    previewText: t.previewText || "",
    body: t.body || "",
    ctaText: t.ctaText || "",
    ctaUrl: t.ctaUrl || "",
    whatsappContentSid: t.whatsappContentSid || "",
    whatsappVariables: t.whatsappVariables || [],
    pushUrl: t.pushUrl || "",
  };
}

export default function TemplatesTab() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [testTo, setTestTo] = useState("");
  const [bodyMode, setBodyMode] = useState<"blocks" | "markup">("blocks");
  const [blocks, setBlocks] = useState<TemplateBlock[]>([]);
  const [insertRequest, setInsertRequest] = useState<{ token: string; nonce: number } | null>(null);
  const [previewWidth, setPreviewWidth] = useState<number>(640);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const { data, isLoading, error } = useQuery<TemplatesResponse>({ queryKey: ["/api/admin/comms/templates"] });
  const templates = data?.templates || [];
  const variables = data?.variables || [];

  const filtered = useMemo(
    () => (channelFilter === "all" ? templates : templates.filter(t => t.channel === channelFilter)),
    [templates, channelFilter],
  );

  useEffect(() => {
    if (dialogOpen) setTestTo(user?.email || "");
  }, [dialogOpen, user?.email]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/templates"] });

  const previewMutation = useMutation({
    mutationFn: async (id: string) => (await apiRequest("POST", `/api/admin/comms/templates/${id}/preview`, {})).json(),
    onSuccess: (res: TemplatePreview) => {
      setPreview(res);
      setPreviewError(null);
    },
    onError: (e: unknown) => {
      setPreview(null);
      setPreviewError(errorMessage(e));
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string | null; body: Record<string, unknown> }) => {
      const res = payload.id
        ? await apiRequest("PATCH", `/api/admin/comms/templates/${payload.id}`, payload.body)
        : await apiRequest("POST", "/api/admin/comms/templates", payload.body);
      return res.json();
    },
    onSuccess: (res: { template?: Template } | Template) => {
      invalidate();
      const saved = (res as { template?: Template }).template || (res as Template);
      const id = saved?.id || draft.id;
      if (id) {
        setDraft(d => ({ ...d, id }));
        previewMutation.mutate(id);
      }
      toast({ title: draft.id ? "Template saved" : "Template created" });
    },
    onError: (e: unknown) => {
      setFormError(errorMessage(e));
      toast({ title: "Could not save the template", description: errorMessage(e), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await apiRequest("DELETE", `/api/admin/comms/templates/${id}`)).json(),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      toast({ title: "Template deleted" });
    },
    onError: (e: unknown) => toast({ title: "Could not delete the template", description: errorMessage(e), variant: "destructive" }),
  });

  const testMutation = useMutation({
    mutationFn: async (payload: { id: string; to: string }) =>
      (await apiRequest("POST", `/api/admin/comms/templates/${payload.id}/test`, { to: payload.to })).json(),
    onSuccess: (res: { ok?: boolean; provider?: string; providerMessageId?: string; error?: string }) => {
      if (res.ok === false || res.error) {
        toast({ title: `${res.provider || "The provider"} refused the test`, description: res.error || "No reason given.", variant: "destructive" });
        return;
      }
      toast({
        title: "Test sent",
        description: `${res.provider || "Provider"} accepted it${res.providerMessageId ? ` as ${res.providerMessageId}` : ""}.`,
      });
    },
    onError: (e: unknown) => toast({ title: "Test send failed", description: errorMessage(e), variant: "destructive" }),
  });

  const openCreate = () => {
    setDraft({ ...EMPTY_DRAFT, channel: channelFilter === "all" ? "email" : (channelFilter as Channel) });
    setBlocks([]);
    setBodyMode("blocks");
    setPreview(null);
    setPreviewError(null);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (t: Template) => {
    setDraft(toDraft(t));
    setBlocks(parseBlocks(t.body || ""));
    setBodyMode("blocks");
    setPreview(null);
    setPreviewError(null);
    setFormError(null);
    setDialogOpen(true);
    previewMutation.mutate(t.id);
  };

  /** Blocks are only a view onto the markup: the body stays the single source of truth. */
  const applyBlocks = (next: TemplateBlock[]) => {
    setBlocks(next);
    setDraft(d => ({ ...d, body: serializeBlocks(next) }));
  };

  const switchMode = (mode: "blocks" | "markup") => {
    if (mode === bodyMode) return;
    if (mode === "blocks") setBlocks(parseBlocks(draft.body));
    setBodyMode(mode);
  };

  const insertVariable = (name: string) => {
    const token = `{{${name}}}`;
    if (bodyMode === "blocks") {
      setInsertRequest(r => ({ token, nonce: (r?.nonce || 0) + 1 }));
      return;
    }
    const el = bodyRef.current;
    if (!el) {
      setDraft(d => ({ ...d, body: `${d.body}${token}` }));
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const next = `${el.value.slice(0, start)}${token}${el.value.slice(end)}`;
    setDraft(d => ({ ...d, body: next }));
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const submit = () => {
    setFormError(null);
    if (!draft.name.trim()) {
      setFormError("Give the template a name.");
      return;
    }
    if (!draft.body.trim()) {
      setFormError("The body cannot be empty.");
      return;
    }
    if (draft.channel === "email" && !draft.subject.trim()) {
      setFormError("Email templates need a subject line.");
      return;
    }
    if (isDeviceChannel(draft.channel) && !draft.subject.trim()) {
      setFormError("A push or in-app notice needs a title. The subject line is the title.");
      return;
    }
    const isEmail = draft.channel === "email";
    const isWhatsapp = draft.channel === "whatsapp";
    const isDevice = isDeviceChannel(draft.channel);
    saveMutation.mutate({
      id: draft.id,
      body: {
        name: draft.name.trim(),
        channel: draft.channel,
        category: draft.category,
        subject: isEmail || isDevice ? draft.subject.trim() : null,
        previewText: isEmail ? draft.previewText.trim() || null : null,
        body: draft.body,
        ctaText: isEmail ? draft.ctaText.trim() || null : null,
        ctaUrl: isEmail ? draft.ctaUrl.trim() || null : null,
        whatsappContentSid: isWhatsapp ? draft.whatsappContentSid.trim() || null : null,
        whatsappVariables: isWhatsapp ? draft.whatsappVariables.map(v => v.trim()).filter(Boolean) : [],
        pushUrl: isDevice ? draft.pushUrl.trim() || null : null,
      },
    });
  };

  const isEmail = draft.channel === "email";
  const isWhatsapp = draft.channel === "whatsapp";
  const isDevice = isDeviceChannel(draft.channel);

  const setWhatsappVariable = (index: number, value: string) =>
    setDraft(d => ({ ...d, whatsappVariables: d.whatsappVariables.map((v, i) => (i === index ? value : v)) }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Templates</CardTitle>
              <CardDescription>
                Bodies are plain text. A blank line starts a paragraph, **bold** is bold, [text](https://...) is a link, lines starting with "- " become
                bullets, "## " and "### " are headings, "&gt; " is a callout, "---" is a rule, and {"{{variable}}"} placeholders are filled per contact at
                send time. The block editor writes all of that for you.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="w-[150px]" aria-label="Filter by channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                  {CHANNELS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={openCreate} data-testid="button-new-template">
                <Plus className="w-4 h-4 mr-2" /> New template
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <ErrorState error={error} />
          ) : isLoading ? (
            <TableSkeleton rows={6} cols={5} />
          ) : filtered.length === 0 ? (
            <EmptyState
              title="No templates here"
              hint={channelFilter === "all"
                ? "Nothing has been written yet. Seeding the default automations on the Automations tab also creates a starter template for each one."
                : `No ${channelLabel(channelFilter).toLowerCase()} template exists yet. Switch the channel filter, or write one.`}
              action={<Button size="sm" onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> New template</Button>}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(t => (
                    <TableRow key={t.id} data-testid={`row-template-${t.id}`}>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell><Badge variant="outline">{channelLabel(t.channel)}</Badge></TableCell>
                      <TableCell>
                        <Badge className={`border-0 ${t.category === "marketing" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"}`}>
                          {t.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{t.subject || "n/a"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(t.updatedAt)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" title="Open" onClick={() => openEdit(t)} data-testid={`button-edit-template-${t.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Delete" onClick={() => setDeleteTarget(t)} data-testid={`button-delete-template-${t.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft.id ? "Edit template" : "New template"}</DialogTitle>
            <DialogDescription>
              The preview and the test send both use the last saved version, so save before you check your latest edit.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-5 lg:grid-cols-2">
            {/* Editor */}
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="template-name">Name</Label>
                  <Input
                    id="template-name"
                    value={draft.name}
                    onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                    placeholder="e.g. Weekly progress, parents"
                    data-testid="input-template-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="template-channel">Channel</Label>
                  <Select value={draft.channel} onValueChange={v => setDraft(d => ({ ...d, channel: v as Channel }))}>
                    <SelectTrigger id="template-channel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CHANNELS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="template-category">Category</Label>
                <Select value={draft.category} onValueChange={v => setDraft(d => ({ ...d, category: v as Category }))}>
                  <SelectTrigger id="template-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{CATEGORIES.find(c => c.id === draft.category)?.hint}</p>
              </div>

              {(isEmail || isDevice) && (
                <div className="space-y-1.5">
                  <Label htmlFor="template-subject">{isDevice ? "Title" : "Subject"}</Label>
                  <Input
                    id="template-subject"
                    value={draft.subject}
                    onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))}
                    placeholder="e.g. {{childFirstName}} moved up this week"
                    data-testid="input-template-subject"
                  />
                  {isDevice && (
                    <p className="text-xs text-muted-foreground">
                      On push and in-app the subject is the title, and the body is delivered as plain text with the markup stripped out. Keep both
                      short.
                    </p>
                  )}
                </div>
              )}

              {isEmail && (
                <div className="space-y-1.5">
                  <Label htmlFor="template-preview-text">Preview text</Label>
                  <Input
                    id="template-preview-text"
                    value={draft.previewText}
                    onChange={e => setDraft(d => ({ ...d, previewText: e.target.value }))}
                    placeholder="The line the inbox shows after the subject"
                    data-testid="input-template-preview-text"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="template-body">Body</Label>
                  <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Body editor mode">
                    <Button
                      type="button"
                      size="sm"
                      variant={bodyMode === "blocks" ? "secondary" : "ghost"}
                      className="h-7 px-2 text-xs"
                      onClick={() => switchMode("blocks")}
                      data-testid="button-body-blocks"
                    >
                      <LayoutList className="w-3.5 h-3.5 mr-1" /> Blocks
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={bodyMode === "markup" ? "secondary" : "ghost"}
                      className="h-7 px-2 text-xs"
                      onClick={() => switchMode("markup")}
                      data-testid="button-body-markup"
                    >
                      <Code2 className="w-3.5 h-3.5 mr-1" /> Markup
                    </Button>
                  </div>
                </div>

                {bodyMode === "blocks" ? (
                  <BlockEditor blocks={blocks} onChange={applyBlocks} insertRequest={insertRequest} />
                ) : (
                  <Textarea
                    id="template-body"
                    ref={bodyRef}
                    value={draft.body}
                    onChange={e => setDraft(d => ({ ...d, body: e.target.value }))}
                    className="font-mono text-xs min-h-[220px]"
                    spellCheck
                    placeholder={"Hi {{firstName}},\n\nHere is how {{childFirstName}} did this week.\n\n- Practised 4 days\n- Strongest topic: {{strongestTopic}}"}
                    data-testid="textarea-template-body"
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  Both edit the same thing. Blocks write the markup for you; Markup is the markup itself. Switching back and forth keeps whatever
                  the blocks can express, and anything they cannot name survives as a paragraph.
                </p>
              </div>

              {variables.length > 0 && (
                <div className="space-y-1.5">
                  <Label>Variables</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {variables.map(v => (
                      <Button
                        key={v.name}
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 font-mono text-xs"
                        title={v.description}
                        onClick={() => insertVariable(v.name)}
                        data-testid={`button-variable-${v.name}`}
                      >
                        {`{{${v.name}}}`}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Click one to drop it in at the cursor.</p>
                </div>
              )}

              {isWhatsapp && (
                <div className="rounded-md border p-3 space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="template-wa-sid">Twilio Content SID</Label>
                    <Input
                      id="template-wa-sid"
                      value={draft.whatsappContentSid}
                      onChange={e => setDraft(d => ({ ...d, whatsappContentSid: e.target.value }))}
                      placeholder="HX..."
                      className="font-mono text-xs"
                      data-testid="input-whatsapp-content-sid"
                    />
                    <p className="text-xs text-muted-foreground">
                      The approved Content Template in Twilio. Without it, WhatsApp only lets us reply inside a 24-hour window.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Template variables, in order</Label>
                    {draft.whatsappVariables.length === 0 && (
                      <p className="text-xs text-muted-foreground">None yet. Add one for each placeholder in the approved template.</p>
                    )}
                    {draft.whatsappVariables.map((name, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <span className="text-xs font-mono text-muted-foreground w-10 shrink-0">{`{{${i + 1}}}`}</span>
                        <Input
                          value={name}
                          onChange={e => setWhatsappVariable(i, e.target.value)}
                          placeholder="e.g. childFirstName"
                          className="font-mono text-xs"
                          data-testid={`input-whatsapp-variable-${i}`}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          title="Remove"
                          onClick={() => setDraft(d => ({ ...d, whatsappVariables: d.whatsappVariables.filter((_, x) => x !== i) }))}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setDraft(d => ({ ...d, whatsappVariables: [...d.whatsappVariables, ""] }))}
                      data-testid="button-add-whatsapp-variable"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add variable
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      {"{{1}}, {{2}} in the approved Twilio template map to these, in order"}
                    </p>
                  </div>
                </div>
              )}

              {isDevice && (
                <div className="space-y-1.5">
                  <Label htmlFor="template-push-url">Deep link</Label>
                  <Input
                    id="template-push-url"
                    value={draft.pushUrl}
                    onChange={e => setDraft(d => ({ ...d, pushUrl: e.target.value }))}
                    placeholder="/dashboard or https://edufocalacademy.com/lessons"
                    className="font-mono text-xs"
                    data-testid="input-push-url"
                  />
                  <p className="text-xs text-muted-foreground">Where tapping the notice takes the child. Leave it blank to open the app as it was.</p>
                </div>
              )}

              {isEmail && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="template-cta-text">Button text</Label>
                    <Input
                      id="template-cta-text"
                      value={draft.ctaText}
                      onChange={e => setDraft(d => ({ ...d, ctaText: e.target.value }))}
                      placeholder="e.g. See the report"
                      data-testid="input-template-cta-text"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="template-cta-url">Button link</Label>
                    <Input
                      id="template-cta-url"
                      value={draft.ctaUrl}
                      onChange={e => setDraft(d => ({ ...d, ctaUrl: e.target.value }))}
                      placeholder="https://edufocalacademy.com/parent"
                      data-testid="input-template-cta-url"
                    />
                  </div>
                </div>
              )}

              {formError && <p className="text-sm text-destructive">{formError}</p>}
            </div>

            {/* Preview */}
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium flex items-center gap-2"><Eye className="w-4 h-4" /> Preview</p>
                {draft.channel === "email" && (
                  <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Preview width">
                    {PREVIEW_WIDTHS.map(w => (
                      <Button
                        key={w.id}
                        type="button"
                        size="sm"
                        variant={previewWidth === w.px ? "secondary" : "ghost"}
                        className="h-7 px-2 text-xs"
                        onClick={() => setPreviewWidth(w.px)}
                        data-testid={`button-preview-${w.id}`}
                      >
                        <w.icon className="w-3.5 h-3.5 mr-1" /> {w.label}
                      </Button>
                    ))}
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                  disabled={!draft.id || previewMutation.isPending}
                  onClick={() => draft.id && previewMutation.mutate(draft.id)}
                  data-testid="button-refresh-preview"
                >
                  {previewMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Refresh
                </Button>
              </div>

              {!draft.id ? (
                <div className="border rounded-md p-6 text-sm text-muted-foreground text-center">
                  Save the template first. The preview is rendered by the server from the saved copy, with sample data filled in.
                </div>
              ) : previewError ? (
                <div className="border rounded-md p-4 text-sm text-destructive">{previewError}</div>
              ) : !preview ? (
                <div className="border rounded-md p-6 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Rendering…
                </div>
              ) : draft.channel === "email" ? (
                <div className="space-y-2">
                  {preview.subject ? (
                    <div className="border rounded-md px-3 py-2 text-sm">
                      <span className="text-muted-foreground">Subject: </span>
                      <span className="font-medium">{preview.subject}</span>
                    </div>
                  ) : null}
                  <div className="flex justify-center bg-muted/30 rounded-md p-2 overflow-x-auto">
                    <iframe
                      title="Email preview"
                      sandbox=""
                      srcDoc={preview.html || "<p>No HTML returned.</p>"}
                      style={{ width: previewWidth, maxWidth: "100%" }}
                      className="h-[420px] border rounded-md bg-white shrink-0"
                      data-testid="iframe-template-preview"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    {previewWidth}px wide, about {previewWidth === 375 ? "a phone" : "a desktop inbox"}.
                  </p>
                </div>
              ) : (
                <pre className="border rounded-md p-3 text-sm whitespace-pre-wrap break-words max-h-[420px] overflow-y-auto bg-muted/30" data-testid="text-template-preview">
                  {preview.text || "No text returned."}
                </pre>
              )}

              <div className="border rounded-md p-3 space-y-2">
                <Label htmlFor="template-test-to">Send test to me</Label>
                <div className="flex gap-2">
                  {!isDevice && (
                    <Input
                      id="template-test-to"
                      value={testTo}
                      onChange={e => setTestTo(e.target.value)}
                      placeholder={draft.channel === "email" ? "you@edufocal.com" : "+18761234567"}
                      data-testid="input-test-to"
                    />
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!draft.id || (!isDevice && !testTo.trim()) || testMutation.isPending}
                    onClick={() => draft.id && testMutation.mutate({ id: draft.id, to: isDevice ? "" : testTo.trim() })}
                    data-testid="button-send-test"
                  >
                    {testMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <SendHorizonal className="w-4 h-4 mr-2" />}
                    Send test
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {isDevice
                    ? "A push or in-app test goes to your own devices and your own in-app inbox, as the admin signed in right now. There is nowhere else to send it."
                    : "Goes out through the live provider on the real sending domain. It does not touch the queue or any campaign stats."}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button>
            <Button onClick={submit} disabled={saveMutation.isPending} data-testid="button-save-template">
              {saveMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : draft.id ? "Save changes" : "Create template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={o => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this template?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name} will be gone. Campaigns and automations pointing at it will not be able to send until another template is picked.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete-template"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
